import { HighlightOff } from '@mui/icons-material';
import { Grid } from '@mui/material';
import { useCallback, useMemo } from 'react';
import useSWR from 'swr';

import { FormPart } from '@modules/shared/components/FormPart';
import { filteredStipType, getOptions } from '@shared/commonFunctions';
import { IFormFieldConfig } from '@shared/components/FormFieldsConstructor';
import { SERVICES } from '@shared/constants/apiConfig';
import { FileDropzone } from '@shared/fields/FileDropzone';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { SaveButton } from '@shared/inputs/Buttons';
import { BorrowerType, IStipType } from '@shared/interfaces';
import { CommonButtonWrapper, PageContent } from '@styles/CommonStyles';
import { useQueryState } from '@shared/hooks/useQueryState';
import { IUploadFilesFormProps } from '../interfaces';

const regexCondition = /[_\' ]/g;

export const UploadFilesForm = ({ value, assetOptions, setFieldValue, setIsValid, dealId }: IUploadFilesFormProps) => {
  const [contactIdParam] = useQueryState('id');

  const { data: stipTypes } = useSWR<IStipType[]>(`${SERVICES.DOCUMENT}/stipTypes?excludeHidden=true`, {revalidateOnMount: undefined});
  const borrowerTypeUrl = useMemo(() => {
    let url = `/${SERVICES.DOCUMENT}/stips/BorrowerTypes/${contactIdParam}`;
    if (dealId) {
        url += `?dealId=${dealId}`;
    }
    return url;
}, [contactIdParam, dealId]);

const { data: borrowerOptions = [] } = useSWR(borrowerTypeUrl);
  
  const stipTypesOptions = useMemo(() => getOptions(filteredStipType(stipTypes)), [stipTypes]);
  const selectStipTypeValue = useCallback(
    (file: File) => {
      const fileName = file?.name?.split('.')?.[0].replace(regexCondition, ' ');
      const fileText = fileName?.toLocaleLowerCase().replace(regexCondition, '');
      const firstWordOfFName = fileName?.split(' ')[0].toLocaleLowerCase();
      const selectedStip = stipTypesOptions.find((stipType) => {
        const stipText = stipType.label.toLocaleLowerCase().replace(regexCondition, '');

        if (stipText === fileText) {
          return stipType;
        }

        return stipType.label?.toLocaleLowerCase().indexOf(firstWordOfFName) >= 0 ?? stipType;
      });

      return selectedStip?.value || '';
    },
    [value?.files]
  );

  const handleFilesChange = useCallback(
    (_name: string, acceptedFiles: File[]) => {
      const files = (value.files as File[]) || [];
      const newfiles = files.concat(acceptedFiles);
      setFieldValue('files', newfiles);

      const selectId = selectStipTypeValue(acceptedFiles?.[0]);
      const docs = value.docs || [];
      const newDocs = acceptedFiles.map(() => {

        return { stipTypeId: selectId as string, borrowerCoBorrowerType: '1' };
      });

      setFieldValue('docs', docs.concat(newDocs));
    },
    [value.files, value.docs]
  );

  const handleStipTypeChange = useCallback(
    (value: string, key: number) => {
      const coBorrowerType = stipTypes?.find((s) => s.id === value);
      if (borrowerOptions.find((a: { borrowerType: number; }) => a.borrowerType == BorrowerType.CoBorrower) && coBorrowerType?.isRequiredFromCoBorrower) {
        setFieldValue(`docs[${key}].borrowerCoBorrowerType`,BorrowerType.CoBorrower.toString());
      } else {
        setFieldValue(`docs[${key}].borrowerCoBorrowerType`, BorrowerType.Borrower.toString());
      }
      setFieldValue(`docs[${key}].stipTypeId`, value);
      setFieldValue(`docs[${key}].borrowerCoBorrowerType`, coBorrowerType?.isRequiredFromCoBorrower ? '2' : '1');
    },
    [stipTypes]
  );

  const removeFile = useCallback(
    (key: number) => {
      const filtered = [...value.files];
      filtered.splice(key, 1);
      setFieldValue('files', filtered);

      const docs = [...value.docs];
      docs.splice(key, 1);
      setFieldValue('docs', docs);
    },
    [value.files, value.docs]
  );

  const formFileConfig = useMemo(() => [
    {
      width: '100%',
      name: 'files',
      component: FileDropzone,
      onFileChange: handleFilesChange,
    },
  ], [handleFilesChange]);

  const getForLabel = useCallback((stipTypeId: string) => {
    const stipType: IStipType = stipTypes?.find((s) => s.id === stipTypeId) as IStipType;

    return `For ${stipType.relatedToEntity}`;
  }, [stipTypes]);

  const getFieldFormConfig = useCallback((f: File, index: number) => {
    const key = index;
    const fieldConfig: IFormFieldConfig<IStipType>[] = [
      {
        width: '35%',
        label: f?.name,
        type: 'label',
      },
      {
        width: '10%',
        label: Math.round(f.size / 1000)
          .toString()
          .concat(' kb'),
        type: 'label',
      },
      {
        width: '25%',
        name: `docs[${key}].stipTypeId`,
        label: 'Type',
        options: stipTypesOptions,
        component: SelectAutocompleteField,
        onChange: (value: string) => handleStipTypeChange(value, key),
      },
    ];
    const stipTypeId = value.docs && value.docs[key] ? value.docs[key].stipTypeId : '';

    if (stipTypeId) {
      setIsValid(false);
      const label = getForLabel(stipTypeId);

      if (label === 'For Contact') {
        fieldConfig.push({
          width: '25%',
          label: '',
          name: `docs[${key}].borrowerCoBorrowerType`,
          options: getOptions(borrowerOptions, 'borrowerType', 'borrowerTypeDesc'),
          component: SelectAutocompleteField,
          hideNoneOption: true,
        });
      }
      else if (label === 'For Asset') {
        if (assetOptions) {
          setIsValid(true);
        }
        fieldConfig.push({
          width: '25%',
          label: ' ',
          placeholder: 'Select Asset',
          name: 'assetId',
          canBeClean: false,
          options: getOptions(assetOptions, 'id', 'display'),
          component: SelectAutocompleteField,
          hideNoneOption: true,
        });
      }
      else {
        fieldConfig.push({
          width: '25%',
          label: label,
          type: 'label',
        });
      }
    } else {
      fieldConfig.push({
        width: '25%',
        label: ' ',
        type: 'label',
      });
    }

    fieldConfig.push({
      width: '5%',
      children: 'Pull',
      size: 'small',
      component: HighlightOff,
      onClick: () => removeFile(key),
    });

    return fieldConfig;
  }, [
    stipTypesOptions,
    borrowerOptions,
    value.docs,
    assetOptions,
    getForLabel,
    removeFile,
    handleStipTypeChange
  ]);

  const getFormFields = useCallback(() => {
    const retVal: IFormFieldConfig<IUploadFilesFormProps>[][] = [];
    if (value.files && value.files.length > 0) {
      value.files.forEach((f: File, index: number) => {
        retVal.push(getFieldFormConfig(f, index));
      });
    }

    return retVal;
  }, [value.files, getFieldFormConfig]);

  return (
    <>
      <PageContent>
        <Grid container>
          <Grid
            item
            md={12}
          >
            <FormPart config={formFileConfig} />
            {getFormFields().map((config: IFormFieldConfig<IUploadFilesFormProps>[]) => (
              <FormPart
                key={`formPart-${config[0].label}-${config[1].label}`}
                config={config}
              />
            ))}
          </Grid>
        </Grid>
      </PageContent>

      <CommonButtonWrapper>
        <SaveButton disabled={!value.docs.length} />
      </CommonButtonWrapper>
    </>
  );
};
